import React, { useState, useEffect, useCallback } from 'react';
import { invAdjustmentService, invItemService, invUnitService, invWastageService } from '../../services/inventoryService';
import { card, pageWrap, btn, inputStyle, selectStyle, label, fieldGroup, modal, modalBox,
  sectionTitle, errorBox, emptyState, fmtQty, fmtDate, friendlyErr } from './inventoryStyles';

const REASONS = ['spoilage','damage','physical_count','internal_usage','theft_loss','opening_correction','other'];
const REASON_LABELS = { spoilage: 'Spoilage', damage: 'Damage', physical_count: 'Physical Count Correction', internal_usage: 'Internal Usage', theft_loss: 'Theft / Loss', opening_correction: 'Opening Correction', other: 'Other' };

const Adjustments = ({ restaurant }) => {
  const [adjustments, setAdjustments] = useState([]);
  const [items, setItems]   = useState([]);
  const [units, setUnits]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ inventory_item_id: '', adjustment_type: 'decrease', reason: 'damage', quantity: '', unit_id: '', notes: '' });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const [aRes, iRes, uRes] = await Promise.all([
      invAdjustmentService.getAll(restaurantId),
      invItemService.getAll(restaurantId),
      invUnitService.getAll(restaurantId),
    ]);
    setAdjustments(aRes.data || []);
    setItems((iRes.data || []).filter(i => i.is_active));
    setUnits(uRes.data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  // Preview stock before/after when item + qty selected
  useEffect(() => {
    if (!form.inventory_item_id || !form.quantity) { setPreview(null); return; }
    const item = items.find(i => i.id === form.inventory_item_id);
    if (!item) return;
    const qty = parseFloat(form.quantity) || 0;
    const before = item.current_stock;
    const after = form.adjustment_type === 'increase' ? before + qty : Math.max(before - qty, 0);
    setPreview({ before, after, name: item.name });
  }, [form.inventory_item_id, form.quantity, form.adjustment_type, items]);

  // Adjustment reasons that should also appear in wastage
  const WASTAGE_REASONS = new Set(['damage', 'spoilage', 'theft_loss', 'internal_usage']);
  // Map adjustment reason → wastage reason
  const WASTAGE_REASON_MAP = { damage: 'damaged', spoilage: 'spoilage', theft_loss: 'theft_loss', internal_usage: 'internal_usage' };

  const save = async () => {
    if (!form.inventory_item_id) { setError('Select an item.'); return; }
    if (!form.quantity || parseFloat(form.quantity) <= 0) { setError('Quantity must be > 0.'); return; }
    if (!form.reason) { setError('Reason is required.'); return; }
    setSaving(true); setError('');
    const { error: e } = await invAdjustmentService.create(restaurantId, form);
    if (e) { setSaving(false); setError(friendlyErr(e)); return; }

    // Auto-log to wastage for decrease adjustments with wastage-type reasons
    if (form.adjustment_type === 'decrease' && WASTAGE_REASONS.has(form.reason)) {
      await invWastageService.create(restaurantId, {
        inventory_item_id: form.inventory_item_id,
        quantity:          form.quantity,
        unit_id:           form.unit_id,
        reason:            WASTAGE_REASON_MAP[form.reason],
        notes:             form.notes || '',
        wastage_date:      new Date().toISOString().slice(0, 10),
      });
    }

    setSaving(false);
    setShowForm(false); load();
  };

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Stock Adjustments</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>Manual corrections — all adjustments are audited</p>
          </div>
          <button style={btn('primary')} onClick={() => { setForm({ inventory_item_id: '', adjustment_type: 'decrease', reason: 'damage', quantity: '', unit_id: '', notes: '' }); setPreview(null); setError(''); setShowForm(true); }}>
            + New Adjustment
          </button>
        </div>

        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={emptyState}>Loading…</div>
          ) : adjustments.length === 0 ? (
            <div style={emptyState}>
              <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No adjustments yet</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                  {['Date', 'Item', 'Type', 'Reason', 'Qty', 'Before', 'After', 'Notes'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adjustments.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 14px', color: '#888', fontSize: '0.82rem' }}>{fmtDate(a.created_at)}</td>
                    <td style={{ padding: '12px 14px', fontWeight: '600', color: '#333' }}>{a.inventory_item?.name}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                        background: a.adjustment_type === 'increase' ? '#f0fdf4' : '#fff7ed',
                        color: a.adjustment_type === 'increase' ? '#16a34a' : '#c2410c',
                        border: `1px solid ${a.adjustment_type === 'increase' ? '#bbf7d0' : '#fed7aa'}` }}>
                        {a.adjustment_type === 'increase' ? '+ Increase' : '− Decrease'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{REASON_LABELS[a.reason] || a.reason}</td>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: a.adjustment_type === 'increase' ? '#16a34a' : '#c2410c' }}>
                      {a.adjustment_type === 'increase' ? '+' : '−'}{fmtQty(a.quantity)}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{fmtQty(a.stock_before)}</td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{fmtQty(a.stock_after)}</td>
                    <td style={{ padding: '12px 14px', color: '#888', fontSize: '0.82rem' }}>{a.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{ ...modalBox, maxWidth: '460px' }}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>Stock Adjustment</h3>
            {error && <div style={errorBox}>{error}</div>}

            <div style={fieldGroup}>
              <label style={label}>Item *</label>
              <select style={selectStyle(!form.inventory_item_id)} value={form.inventory_item_id} onChange={e => setForm(f => ({ ...f, inventory_item_id: e.target.value }))}>
                <option value="">Select item…</option>
                {items.map(i => <option key={i.id} value={i.id}>{i.name} (stock: {fmtQty(i.current_stock)})</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div style={fieldGroup}>
                <label style={label}>Adjustment Type *</label>
                <select style={selectStyle()} value={form.adjustment_type} onChange={e => setForm(f => ({ ...f, adjustment_type: e.target.value }))}>
                  <option value="increase">Increase Stock (+)</option>
                  <option value="decrease">Decrease Stock (−)</option>
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Reason *</label>
                <select style={selectStyle()} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}>
                  {REASONS.map(r => <option key={r} value={r}>{REASON_LABELS[r]}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Quantity *</label>
                <input style={inputStyle(!form.quantity)} type="number" min="0.001" step="0.001" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="0.000" />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Unit</label>
                <select style={selectStyle()} value={form.unit_id} onChange={e => setForm(f => ({ ...f, unit_id: e.target.value }))}>
                  <option value="">Base unit</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.short_code}</option>)}
                </select>
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={label}>Notes</label>
              <textarea style={{ ...inputStyle(), height: '72px', resize: 'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Describe reason in detail…" />
            </div>

            {preview && (
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '0.88rem' }}>
                <strong>{preview.name}</strong>: {fmtQty(preview.before)} → <strong style={{ color: form.adjustment_type === 'increase' ? '#16a34a' : '#dc2626' }}>{fmtQty(preview.after)}</strong>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Adjustment'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Adjustments;
